<script setup lang="ts">
import { FlyoutWithOverlay, InfiniteListView } from '@/components/shared'
import useGlobalStore from '@/stores/modules/global'
import usePaginationStore from '@/stores/modules/pagination'
import CONSTANTS from '@/utils/constants/string-constants'
import {
  DATE_TIME_FORMAT_12_HRS,
  DIGESTATE_JOBS_DETAILS_URL_NAME,
  EMPLOYEES_URL,
  HAULAGE_JOBS_DETAILS_URL_NAME,
  NOTIFICATIONS_LIST_STORE,
  SPREADING_JOBS_DETAILS_URL_NAME
} from '@/utils/constants'
import { getLocalDateTime } from '@/utils/helpers'
import { onUnmounted, ref } from 'vue'
import useActivityStore from '@/stores/modules/activities'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { BaseButton, BaseDialogBox } from '../base'
import type { IActivity } from '@/interfaces'

const globalStore = useGlobalStore()
const listStore = usePaginationStore(NOTIFICATIONS_LIST_STORE)
const activityStore = useActivityStore()
const router = useRouter()
const showDialog = ref(false)
const isJobDeleted = ref()

const getJobRoute = async (listItem: IActivity) => {
  if (listItem.haulageJobId) {
    isJobDeleted.value = listItem.haulageJob?.archivedAt
    return { name: HAULAGE_JOBS_DETAILS_URL_NAME, params: { id: listItem.haulageJobId } }
  } else if (listItem.digestateJobId) {
    isJobDeleted.value = listItem.digestateJob?.archivedAt
    return { name: DIGESTATE_JOBS_DETAILS_URL_NAME, params: { id: listItem.digestateJobId } }
  } else if (listItem.spreadingJobId) {
    isJobDeleted.value = listItem.spreadingJob?.archivedAt
    return { name: SPREADING_JOBS_DETAILS_URL_NAME, params: { id: listItem.spreadingJobId } }
  }
  return { path: '/' }
}

const handleJobClick = async (listItem: IActivity, event: MouseEvent) => {
  globalStore.showNotifications = false
  const route = await getJobRoute(listItem)
  if (isJobDeleted.value) {
    event.preventDefault()
    showDialog.value = true
    isJobDeleted.value = null
  } else {
    router.push(route as RouteLocationRaw)
  }
}

onUnmounted(() => {
  listStore.$reset()
})
</script>
<template>
  <FlyoutWithOverlay v-model="globalStore.showNotifications" flyout-class="d-flex flex-column">
    <template #flyout-header>
      <h6 class="heading-3 semibold">{{ CONSTANTS.NOTIFICATIONS }}</h6>
    </template>
    <div class="d-flex flex-column flex-grow-1 overflow-y--hidden">
      <div
        v-if="listStore.noData"
        class="d-flex flex-column ga-3 align-center justify-center no-notifications-wrapper"
      >
        <span class="bg-blue_50 notification--icon-wrapper">
          <v-icon icon="mdi-bell" size="18" color="blue_500" />
        </span>
        <span class="body-1--new">{{ CONSTANTS.NO_NOTIFICATIONS }}</span>
      </div>
      <InfiniteListView
        module-name="notifications"
        reset-on-unmount
        :list-name="NOTIFICATIONS_LIST_STORE"
        :list-item-height="100"
        @api-call="activityStore.getNotificationsActivities"
      >
        <template #list-item="{ listItem }">
          <div
            class="d-flex text-decoration-none inherit--text ga-3 align-start px-5 py-4"
            v-if="listItem.haulageJobId || listItem.digestateJobId || listItem.spreadingJobId"
            @click.prevent="(event: MouseEvent) => handleJobClick(listItem, event)"
          >
            <div class="d-flex flex-column ga-1">
              <span class="body-1--new mb-1">
                <span class="bold">{{ listItem.description }}</span>
              </span>
              <!-- <span class="body-1--new mb-1">
                <span class="bold">{{ listItem.title }}</span>
                by
                <router-link
                  v-if="listItem.user"
                  class="text-decoration-none"
                  :to="`${EMPLOYEES_URL}/${listItem.user.id}/edit`"
                  >{{ listItem.user.name }}</router-link
                >
              </span> -->
              {{ getLocalDateTime(listItem.timestamp, DATE_TIME_FORMAT_12_HRS) }}
            </div>
          </div>
        </template>
        <template #list-skeleton-loader>
          <div v-for="(_, index) in 10" :key="index" class="d-flex ga-3 align-start px-3 pt-4">
            <div class="d-flex flex-column flex-grow-1 ga-1">
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="80%" />
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="30%" />
            </div>
          </div>
        </template>
        <template #list-load-more-loader>
          <div class="d-flex ga-3 align-start px-3 pt-4">
            <div class="d-flex flex-column flex-grow-1 ga-1">
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="100%" />
              <v-skeleton-loader class="body-1--new mb-1" height="20" width="40%" />
            </div>
          </div>
        </template>
      </InfiniteListView>
    </div>
  </FlyoutWithOverlay>
  <BaseDialogBox v-model="showDialog" v-bind="$attrs">
    <v-card
      :min-width="360"
      :max-width="$attrs['max-width'] ? ($attrs['max-width'] as string | number) : 480"
    >
      <v-card-title id="dialog-title" class="pt-7 pb-3 px-5">
        <h4 class="heading-3 bold black--text" :style="{ '-webkit-line-clamp': 2 }">
          <slot name="confirmation-title">
            {{ CONSTANTS.JOB_NOT_FOUND }}
          </slot>
        </h4>
      </v-card-title>
      <v-card-subtitle class="body-1 mt-0 mb-4 px-5 py-0 black--text">
        <slot name="confirmation-message">
          {{ CONSTANTS.JOB_DELETED_TEXT }}
        </slot>
      </v-card-subtitle>
      <hr v-if="$slots['confirmation-actions']" class="separator" />
      <v-card-actions class="card-actions d-flex pb-4 px-4">
        <slot name="confirmation-actions">
          <BaseButton
            outlined
            plain
            height="40"
            class="bg-slate_100 cancel body-1 px-6 ml-auto"
            @click="() => (showDialog = false)"
          >
            {{ CONSTANTS.OKAY }}
          </BaseButton>
        </slot>
      </v-card-actions>
    </v-card>
  </BaseDialogBox>
</template>
<style scoped lang="scss">
.circular-loader {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.no-notifications-wrapper {
  width: 100%;
  min-height: 180px;
  .notification--icon-wrapper {
    padding: 4px 8px;
    border-radius: 2px;
  }
}
.notification--icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;

  &[type='1'] {
    background-color: rgb(var(--v-theme-emerald_50));
  }
  &[type='2'] {
    background-color: rgb(var(--v-theme-blue_50));
  }
  &[type='3'] {
    background-color: rgb(var(--v-theme-pink_50));
  }
}
</style>
